/**
 * Temporary chats: a conversation that leaves no trace. Nobody need be in it — or you bring in an
 * agent, or several: they answer with everything they know (their memory, their past conversations)
 * and write nothing of this one down. Each is kept a day, then it is gone.
 */
import { ArrowLeft, MessageSquareDashed, Plus, X } from 'lucide-react'
import { Composer } from './Agents'
import { Thread } from './Thread'
import type { Agent, Session } from './data'
import { Btn, Card, Glyph, Glyphs, I, SectionHead, Stack, cx, popAt, type Palette, type PopEntry } from './kit'
import { sessionMenu } from './menus'
import { Bar } from './Page'
import { useDemo } from './store'

const DAY = 24 * 60 * 60_000
/** How long a temporary chat still has. */
const left = (c: Session) => {
  const ms = c.at + DAY - Date.now()
  return ms <= 0 ? 'Gone soon' : ms < 60 * 60_000 ? `${Math.max(1, Math.round(ms / 60_000))} min left` : `${Math.round(ms / (60 * 60_000))} h left`
}

/** Who can be brought in: every agent, ticked when in — and each group chat, to bring its people at once. */
function people(): PopEntry[] {
  const s = useDemo.getState()
  const now = s.tempId ? (s.temp.find((x) => x.id === s.tempId)?.with ?? []) : s.tempWith
  const all = (ids: string[]) => ids.every((id) => now.includes(id))
  return [
    ...s.agents.map((a) => ({ label: a.name, icon: <Glyph palette={a.palette} />, hint: a.label, checked: now.includes(a.id), keep: true, onSelect: () => useDemo.getState().toggleTemp(a.id) })),
    ...(s.groups.length ? ['rule' as const] : []),
    ...s.groups.filter((g) => g.members.length).map((g) => ({ label: `Everyone in ${g.name}`, fixed: true, keep: true, checked: all(g.members), onSelect: () => g.members.filter((m) => all(g.members) || !now.includes(m)).forEach((m) => useDemo.getState().toggleTemp(m)) })),
  ]
}

/** The agents in a temporary chat, each a small chip that can be taken out, and a way to bring more in. */
function With({ ids }: { ids: string[] }) {
  const agents = useDemo((s) => s.agents)
  const toggle = useDemo((s) => s.toggleTemp)
  const inIt = ids.map((id) => agents.find((a) => a.id === id)).filter((a): a is Agent => !!a)
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {inIt.map((a) => (
        <span key={a.id} className="flex h-8 items-center gap-2 rounded-[8px] border border-ws-hair bg-ws-well pl-[7px] pr-1 text-ws-fg-2">
          <Glyph palette={a.palette} />
          {a.name}
          <button type="button" aria-label={`Take ${a.name} out`} onClick={() => toggle(a.id)} className="flex h-6 w-6 items-center justify-center rounded-[6px] text-ws-icon transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg">
            <X size={13} strokeWidth={2} />
          </button>
        </span>
      ))}
      <button type="button" onClick={(e) => popAt(e.currentTarget, people)} className="flex h-8 items-center gap-2 rounded-[8px] border border-ws-hair bg-ws-well pl-[7px] pr-[10px] text-ws-fg-2 transition-colors duration-150 hover:border-ws-edge hover:bg-ws-hover hover:text-ws-fg">
        <I icon={Plus} className="text-ws-icon" />
        {inIt.length ? 'Add' : 'Bring an agent in'}
      </button>
    </div>
  )
}

export function ChatsBar() {
  const tempId = useDemo((s) => s.tempId)
  const tempWith = useDemo((s) => s.tempWith)
  const found = useDemo((s) => s.temp.find((x) => x.id === s.tempId))
  const session = tempId ? (found ?? { id: tempId, agentId: null, title: '', with: tempWith, at: Date.now(), preview: '', busy: true, pinned: false, unread: false, wire: null }) : undefined
  const agents = useDemo((s) => s.agents)
  const openTemp = useDemo((s) => s.openTemp)
  const newTemp = useDemo((s) => s.newTemp)
  const count = useDemo((s) => s.temp.length)
  if (!session)
    return (
      <Bar>
        <span className="pl-2 text-ws-fg-2">
          {count} {count === 1 ? 'chat' : 'chats'} · each is gone after a day
        </span>
      </Bar>
    )
  const inIt = session.with.map((id) => agents.find((a) => a.id === id)).filter((a): a is Agent => !!a)
  return (
    <Bar
      right={
        <>
          <span className="mr-3 hidden text-[12px] tracking-normal text-ws-fg-3 sm:block">{left(session)}</span>
          <Btn icon={Plus} onClick={() => newTemp()}>
            <span className="hidden md:inline">New temporary chat</span>
          </Btn>
        </>
      }
    >
      <Btn icon={ArrowLeft} onClick={() => openTemp(null)}>
        <span className="hidden sm:inline">Temporary chats</span>
      </Btn>
      <span className="mx-2 h-4 w-px shrink-0 bg-ws-line" />
      {/* who is in it — press to bring someone in or take someone out */}
      <button type="button" onClick={(e) => popAt(e.currentTarget, people)} className="flex h-8 min-w-0 items-center gap-2 rounded-[8px] px-2 text-ws-fg-2 transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg">
        {inIt.length ? <Stack palettes={inIt.map((a) => a.palette)} /> : <I icon={MessageSquareDashed} className="text-ws-icon" />}
        <span className="truncate">{inIt.length ? inIt.map((a) => a.name).join(', ') : 'No agent'}</span>
      </button>
    </Bar>
  )
}

export function ChatsView() {
  const temp = useDemo((s) => s.temp)
  const tempId = useDemo((s) => s.tempId)
  const tempWith = useDemo((s) => s.tempWith)
  const agents = useDemo((s) => s.agents)
  const openTemp = useDemo((s) => s.openTemp)
  if (tempId) return <Thread temp />
  const list = temp
  const names = tempWith.map((id) => agents.find((a) => a.id === id)?.name).filter(Boolean)
  const palette = (id: string): Palette => agents.find((a) => a.id === id)?.palette ?? 'syntax'
  return (
    <div className="flex min-h-full flex-col">
      <section className="flex flex-col items-center px-4 md:px-0">
        <h1 className="m-0 mt-10 text-center font-ws-serif text-[28px] font-normal leading-[36px] tracking-normal text-ws-fg md:mt-[78px] md:text-[33px] md:leading-[40px]">A blank page</h1>
        <p className="m-0 mt-3 text-center text-[12px] leading-4 tracking-normal text-ws-fg-3 md:mt-4">{names.length ? `${names.join(names.length === 2 ? ' and ' : ', ')} will answer with what ${names.length === 1 ? 'it knows' : 'they know'}, and remember nothing of this` : 'Nothing said here is remembered, and it is gone tomorrow'}</p>
        <div className="mt-7 w-full max-w-[800px] md:mt-9">
          <Composer />
        </div>
        <div className="mt-4 w-full max-w-[800px] md:mt-[27px]">
          <With ids={tempWith} />
        </div>
      </section>
      {list.length > 0 && (
        <section className="mt-auto px-4 pb-6 pt-12 md:px-10 md:pb-[39px]">
          <SectionHead title="Still here" count={list.length} />
          <div className="ws-noscroll mt-5 flex gap-3 overflow-x-auto pb-px lg:grid lg:grid-cols-4 lg:overflow-visible">
            {list.map((c) => (
              <Card key={c.id} as="button" onClick={() => openTemp(c.id)} menu={() => sessionMenu(c.id)} className="h-[68px] w-[260px] shrink-0 pl-3 pr-2 pt-3 lg:w-auto">
                <span className="flex h-4 items-center">
                  {c.with.length ? <Glyphs palettes={c.with.map(palette)} max={5} /> : <I icon={MessageSquareDashed} className="text-ws-icon" />}
                  <span className={cx('ml-auto pl-2 pr-1 text-[12px] leading-4 tracking-normal text-ws-fg-4')}>{left(c)}</span>
                </span>
                <span className="mt-[11px] block truncate text-[12px] leading-4 tracking-normal text-ws-fg-2">{c.title}</span>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
